import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {Validators} from '@angular/forms';
import {SelectOptions} from '@aviary/store/common/common.types';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {FormDialogComponent} from '@aviary/dialogs/form-dialog/form-dialog.component';
import {FormDialog, FormDialogDefinition} from '@aviary/dialogs/form-dialog/form-dialog.types';
import {ExtendedFormBuilder} from '@aviary/forms/extensions/extended-form-builder/extended-form-builder';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {RadioGroupFormFieldComponent} from '@aviary/forms/fields/radio-group-form-field/radio-group-form-field.component';
import {TextareaFormFieldComponent} from '@aviary/forms/fields/textarea-form-field/textarea-form-field.component';
import {CheckoutClaimType, CheckoutDialogData, FileClaimValue} from '@store/checkouts/checkouts.types';
import {CheckoutsSelectors} from '@store/checkouts/checkouts.selectors';
import {CheckoutDetailComponent} from '@app/my-account/checked-out/detail/checkout-detail.component';

@Component({
  selector: 'px-file-claim-dialog',
  template: `
    <mx-form-dialog [definition]="dialogDefinition">
      <px-checkout-detail [checkout]="checkout"/>
      <mx-radio-group-form-field fieldName="type" [vertical]="true" [options]="claimTypeSelectOptions" [formGroup]="formGroup"/>
      @if (typeSelected()) {
        <div class="text-body-medium">
          <div class="pb-2">
            {{
              formGroup.value.type === 'CLAIMED_RETURNED' ?
                'Please provide any relevant details, such as when the item was returned and what location it was returned to:' :
                'Please provide any relevant details, such as if you checked out any items on the same day and what location you most recently visited:'
            }}
          </div>
          <mx-textarea-form-field fieldName="patronComment" overrideLabel="" placeholder="Enter details here..." [formGroup]="formGroup"/>
        </div>
      }
    </mx-form-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CheckoutDetailComponent, FormDialogComponent, RadioGroupFormFieldComponent, TextareaFormFieldComponent]
})
export class FileClaimDialog implements FormDialog<CheckoutDialogData, FileClaimValue> {
  public readonly selectors = CheckoutsSelectors.StatelessClaims;
  public readonly dialogData: CheckoutDialogData = inject(MAT_DIALOG_DATA);
  public readonly checkout = this.dialogData.checkout;

  public readonly claimTypeSelectOptions: SelectOptions<CheckoutClaimType> = [
    {displayName: 'I returned this item.', value: 'CLAIMED_RETURNED'},
    {displayName: 'I never checked out this item.', value: 'CLAIMED_NEVER_HAD'},
  ];

  public typeSelected(): boolean {
    return isPopulatedString(this.formGroup.value.type);
  }

  public readonly formGroup: ExtendedFormGroup = inject(ExtendedFormBuilder).nonNullable.extendedGroup({
    type: ['', [Validators.required]],
    patronComment: ['', [Validators.maxLength(200)]]
  });

  public readonly dialogDefinition: FormDialogDefinition<FileClaimValue> = {
    title: 'File Claim',
    formGroup: this.formGroup,
    confirmButtonLabel: 'Submit Claim'
  };
}
